import { useState } from 'react';
import { IconHook, IconPlus, IconRefresh, IconSend, IconTrash } from '../components/icons';
import { CopyButton, Empty, Field, Modal, Skeleton, useAsync, useToast } from '../components/ui';
import { api, Webhook, WebhookDelivery, WEBHOOK_EVENTS } from '../lib/api';
import { ago } from '../lib/format';
import { canWrite, useSession } from '../lib/session';
import { t } from '../lib/i18n';

const STATE: Record<WebhookDelivery['state'], [string, string]> = {
  PENDING: ['ожидает', 'pending'],
  DELIVERING: ['отправляется', 'queued'],
  DELIVERED: ['доставлен', 'ok'],
  FAILED: ['ошибка', 'bad'],
};

export function WebhooksPage() {
  const { me } = useSession();
  const toast = useToast();
  const hooks = useAsync(() => api.get<Webhook[]>('/webhooks'));
  const [selected, setSelected] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState<Webhook | null>(null);
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>(['email.delivered', 'email.bounced', 'email.complained']);
  const [format, setFormat] = useState<Webhook['format']>('mailocean');
  const [error, setError] = useState('');
  const current = selected ?? hooks.data?.[0]?.id ?? null;
  const deliveries = useAsync(() => (current ? api.get<WebhookDelivery[]>(`/webhooks/${current}/deliveries`) : Promise.resolve([])), [current]);

  async function create() {
    setError('');
    try {
      const h = await api.post<Webhook>('/webhooks', { url, events, format });
      setCreated(h);
      setOpen(false);
      setUrl('');
      setSelected(h.id);
      hooks.reload();
    } catch (e) {
      setError((e as Error).message);
    }
  }
  async function remove(h: Webhook) {
    if (!confirm(t('Удалить вебхук {url}?', { url: h.url }))) return;
    await api.del(`/webhooks/${h.id}`);
    setSelected(null);
    hooks.reload();
  }
  async function ping(h: Webhook) {
    await api.post(`/webhooks/${h.id}/ping`);
    toast(t('Тестовое событие поставлено в очередь'));
    setTimeout(deliveries.reload, 1500);
  }

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1>{t('Вебхуки')}</h1>
          <p>{t('События о письмах в ваш сервер: доставка, отскоки, жалобы. Подпись:')} <code>MailOcean-Signature: t=…,v1=HMAC-SHA256</code>.</p>
        </div>
        {canWrite(me) && <button className="btn btn-primary" onClick={() => setOpen(true)} data-track="webhook.create_click"><IconPlus size={16} /> {t('Новый эндпоинт')}</button>}
      </div>

      {created?.secret && (
        <div className="card card-pad stack" style={{ marginBottom: 16 }}>
          <h3>{t('Секрет подписи для {url}', { url: created.url })}</h3>
          <div className="secret-box"><code>{created.secret}</code><CopyButton value={created.secret} /></div>
          <div className="callout warn">{t('Секрет показывается один раз. Повторы: до 8 попыток с экспоненциальной паузой.')}</div>
          <div><button className="btn btn-sm" onClick={() => setCreated(null)}>{t('Готово')}</button></div>
        </div>
      )}

      {hooks.loading ? (
        <div className="card card-pad"><Skeleton h={80} /></div>
      ) : !hooks.data?.length ? (
        <div className="card">
          <Empty icon={<IconHook size={36} />} title={t('Эндпоинтов пока нет')} action={<button className="btn btn-primary" onClick={() => setOpen(true)}>{t('Добавить')}</button>}>
            {t('Подключите эндпоинт, чтобы бэкенд узнавал об отскоках и жалобах в реальном времени.')}
          </Empty>
        </div>
      ) : (
        <div className="grid" style={{ gridTemplateColumns: 'minmax(260px, 1fr) 2fr' }}>
          <div className="card">
            {hooks.data.map((h) => (
              <button
                key={h.id}
                onClick={() => setSelected(h.id)}
                className="card-pad"
                style={{ display: 'block', width: '100%', textAlign: 'left', border: 0, borderBottom: '1px solid var(--border)', background: h.id === current ? 'var(--surface-2)' : 'transparent', color: 'inherit', font: 'inherit', cursor: 'pointer', boxShadow: h.id === current ? 'inset 3px 0 0 var(--accent)' : 'none' }}
              >
                <div className="truncate" style={{ fontWeight: 700 }}>{h.url}</div>
                <div className="small muted">{h.format !== 'mailocean' ? <>{t('формат {format}', { format: h.format })} · </> : ''}{t('Событий: {n} · создан {when}', { n: h.events.length, when: ago(h.created_at) })}</div>
              </button>
            ))}
          </div>
          <div className="card">
            <div className="card-head">
              <h3>{t('Доставки')}</h3>
              {(() => {
                const h = hooks.data.find((x) => x.id === current);
                return (
                  h && (
                    <>
                      <button className="btn btn-sm" onClick={deliveries.reload}><IconRefresh size={14} /></button>
                      {canWrite(me) && <button className="btn btn-sm" onClick={() => ping(h)}><IconSend size={14} /> Ping</button>}
                      {canWrite(me) && <button className="btn btn-sm btn-danger" onClick={() => remove(h)}><IconTrash size={14} /></button>}
                    </>
                  )
                );
              })()}
            </div>
            {!deliveries.data?.length ? (
              <Empty title={t('Доставок пока нет')}>{t('Отправьте письмо или нажмите Ping.')}</Empty>
            ) : (
              <table className="table">
                <thead><tr><th>{t('Событие')}</th><th>{t('Состояние')}</th><th>{t('Попыток')}</th><th>{t('Ответ')}</th><th>{t('Когда')}</th></tr></thead>
                <tbody>
                  {deliveries.data.map((d) => (
                    <tr key={d.id}>
                      <td><code>{d.event_type}</code></td>
                      <td><span className={`pill ${STATE[d.state][1]}`}>{t(STATE[d.state][0])}</span></td>
                      <td>{d.attempts}</td>
                      <td className="small muted">{d.last_status ?? d.last_error ?? '—'}</td>
                      <td className="small muted">{ago(d.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {open && (
        <Modal
          title={t('Новый эндпоинт')}
          onClose={() => setOpen(false)}
          footer={
            <>
              <button className="btn" onClick={() => setOpen(false)}>{t('Отмена')}</button>
              <button className="btn btn-primary" onClick={create} disabled={!url || !events.length}>{t('Создать')}</button>
            </>
          }
        >
          <Field label="URL" hint={t('Только https и публичный адрес; редиректы не выполняются')} error={error}>
            <input className="input input-mono" autoFocus placeholder="https://api.example.com/webhooks/mailocean" value={url} onChange={(e) => setUrl(e.target.value)} />
          </Field>
          <Field label={t('События')}>
            <div className="chips">
              {WEBHOOK_EVENTS.map((e) => (
                <button key={e} className={`chip${events.includes(e) ? ' on' : ''}`} onClick={() => setEvents((xs) => (xs.includes(e) ? xs.filter((x) => x !== e) : [...xs, e]))}>{e}</button>
              ))}
            </div>
          </Field>
          <Field label={t('Формат')} hint={t('Переезжаете с SendGrid или Postmark? Выберите их формат — ваш обработчик событий заработает без изменений.')}>
            <div className="chips">
              {([['mailocean', 'MailOcean'], ['sendgrid', 'SendGrid Event Webhook'], ['postmark', 'Postmark']] as const).map(([f, l]) => (
                <button key={f} className={`chip${format === f ? ' on' : ''}`} onClick={() => setFormat(f)}>{l}</button>
              ))}
            </div>
          </Field>
        </Modal>
      )}
    </>
  );
}
